/*
 * Apollon - Logo marquee.
 *
 * The row is driven by scrollLeft rather than a CSS transform. A keyframe on
 * translateX is smoother, but it cannot coexist with a scrollbar: the scroll
 * offset and the transform are two independent positions that add together,
 * so scrubbing a few thousand pixels right dragged the track clean off its
 * own end and left blank space. Sharing one number means there is nothing to
 * reconcile, and touch gets real momentum scrolling for free.
 *
 * A lap is one list plus one gap. At that distance the duplicate list sits
 * exactly where the original started, so the wrap is invisible. It is
 * measured from the rendered DOM, not declared, so the phone breakpoint needs
 * no override - the old keyframe had its own copy and the two drifted apart.
 *
 * TRAP 1, script placement. Shopify's script_tag filter emits no defer, so a
 * tag at the top of a section runs before the element is parsed. The element
 * then upgrades mid-parse, while its own children do not exist yet, and any
 * querySelector in connectedCallback returns null. Rather than depend on the
 * tag sitting after the markup, setup waits for the children to appear.
 *
 * TRAP 2, naming. Do not store the direction on `this.dir`. HTMLElement
 * already owns `dir` for text directionality, so `this.dir = 1` writes
 * dir="1", which is not a valid value, and the getter hands back "". The row
 * then advances by "" * rate, which is 0, and sits perfectly still with no
 * error anywhere. Same applies to title, lang, hidden, translate, slot and
 * draggable.
 */
if (!customElements.get('apollon-marquee')) {
  customElements.define(
    'apollon-marquee',
    class extends HTMLElement {
      connectedCallback() {
        if (this.ready) return;

        // Upgraded before its own markup was parsed. See TRAP 1.
        if (!this.querySelector('.apollon-mq__track')) {
          if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.connectedCallback(), { once: true });
          }
          return;
        }

        this.ready = true;
        this.viewport = this.querySelector('.apollon-mq__viewport');
        this.track = this.querySelector('.apollon-mq__track');

        this.way = this.dataset.direction === 'right' ? -1 : 1;
        this.held = false;
        this.dragging = false;
        this.onscreen = true;
        this.frame = this.frame.bind(this);
        this.measure = this.measure.bind(this);
        this.measure();

        if (window.ResizeObserver) {
          this.ro = new ResizeObserver(this.measure);
          this.ro.observe(this.track);
        }

        if (this.dataset.pause === 'true') {
          this.addEventListener('pointerenter', () => {
            this.held = true;
          });
          this.addEventListener('pointerleave', () => {
            if (!this.dragging) this.held = false;
          });
          this.addEventListener('focusin', () => {
            this.held = true;
          });
          this.addEventListener('focusout', () => {
            this.held = false;
          });
        }

        // Dragging the scrollbar thumb usually means leaving the section,
        // which fires pointerleave and would hand control back to the loop
        // mid-drag. Hold until the pointer is actually released.
        this.viewport.addEventListener('pointerdown', () => {
          this.dragging = true;
          this.held = true;
        });
        this.release = () => {
          if (!this.dragging) return;
          this.dragging = false;
          if (!this.matches(':hover')) this.held = false;
        };
        window.addEventListener('pointerup', this.release);

        // A finger on the strip is the same intent as a cursor over it.
        this.viewport.addEventListener('touchstart', () => { this.held = true; }, { passive: true });
        this.viewport.addEventListener('touchend', () => { this.held = false; });

        // Off screen the loop is pure waste, and it stops a backgrounded page
        // from banking up scroll it would apply all at once.
        if (window.IntersectionObserver) {
          this.io = new IntersectionObserver((entries) => {
            this.onscreen = entries[0].isIntersecting;
            if (this.onscreen) this.start();
          });
          this.io.observe(this);
        }

        this.pos = this.way === -1 ? this.lap : 0;
        this.viewport.scrollLeft = this.pos;
        this.start();
      }

      disconnectedCallback() {
        cancelAnimationFrame(this.raf);
        this.raf = null;
        // The theme editor re-renders a section as a fresh subtree, so this
        // has to be able to set itself up a second time.
        this.ready = false;
        if (this.ro) this.ro.disconnect();
        if (this.io) this.io.disconnect();
        if (this.release) window.removeEventListener('pointerup', this.release);
      }

      measure() {
        const list = this.track.firstElementChild;
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        this.lap = list ? list.getBoundingClientRect().width + gap : 0;
        this.rate = this.lap / (parseFloat(this.dataset.speed) || 40);
      }

      start() {
        if (this.raf || !this.lap) return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        this.last = null;
        this.raf = requestAnimationFrame(this.frame);
      }

      frame(t) {
        this.raf = null;
        if (!this.isConnected || !this.onscreen) return;

        if (this.held) {
          // Follow the hand rather than fight it, so the loop picks up from
          // wherever the scrubbing left off.
          this.pos = this.viewport.scrollLeft;
        } else if (this.last !== null) {
          // Capped, because a tab that was backgrounded hands back one
          // enormous delta and the row would jump most of a lap.
          this.pos += (this.way * this.rate * Math.min(t - this.last, 50)) / 1000;
          while (this.pos >= this.lap) this.pos -= this.lap;
          while (this.pos < 0) this.pos += this.lap;
          this.viewport.scrollLeft = this.pos;
        }

        this.last = t;
        this.raf = requestAnimationFrame(this.frame);
      }
    }
  );
}
